import { HealthCheckName } from "@/gen_src/proto/chalk/server/v1/status_pb";

export interface HealthCheckGroup {
  /** Stable dashboard-customization identifier; the display title may change independently. */
  id: string;
  title: string;
  checks: HealthCheckName[];
  hideInServerless?: boolean;
}

// Health checks grouped by the Kubernetes component / functionality they
// exercise. ConnectionsPane renders one card per group (so unrelated checks
// don't clump together) and the healthcheck-monitor picker renders one
// combobox section per group.
export const HEALTH_CHECK_GROUPS: HealthCheckGroup[] = [
  {
    id: "kube-persistent-storage",
    title: "Kube persistent storage",
    checks: [HealthCheckName.EBS_CSI_DRIVER],
    hideInServerless: true,
  },
  {
    id: "cluster-networking",
    title: "Cluster networking",
    checks: [
      HealthCheckName.ENVOY_PROXY,
      HealthCheckName.GATEWAY,
      HealthCheckName.GATEWAY_DNS,
      HealthCheckName.GATEWAY_ROUTABILITY,
      HealthCheckName.AWS_LOAD_BALANCER_CONTROLLER,
      HealthCheckName.CORE_DNS,
      HealthCheckName.EXTERNAL_DNS_CHART,
      HealthCheckName.VPC_CNI,
    ],
    hideInServerless: true,
  },
  {
    id: "autoscaling-and-nodes",
    title: "Autoscaling & nodes",
    checks: [
      HealthCheckName.KUBERNETES_NODE_VERSIONS,
      HealthCheckName.KARPENTER,
      HealthCheckName.KEDA,
      HealthCheckName.KEDA_COMPONENTS,
      HealthCheckName.SPILLING_CONFIG,
      HealthCheckName.CHALK_MACHINE_TYPE_NODEPOOLS,
      HealthCheckName.OFFLINE_NODEPOOL,
      HealthCheckName.ONLINE_NODEPOOL,
      HealthCheckName.INFRASTRUCTURE_NODEPOOL,
    ],
    hideInServerless: true,
  },
  {
    id: "cluster-addons",
    title: "Cluster add-ons",
    checks: [
      HealthCheckName.CERTIFICATE_MANAGER,
      HealthCheckName.METRICS_SERVER_CHART,
      HealthCheckName.KUBERNETES_METRICS,
      HealthCheckName.KUBERNETES_PROXY_ADDON,
      HealthCheckName.KUBE_PERMISSIONS,
    ],
    hideInServerless: true,
  },
  {
    id: "telemetry-and-logging",
    title: "Telemetry & logging",
    checks: [
      HealthCheckName.LOGGING_CLIENT,
      HealthCheckName.TELEMETRY_COLLECTORS,
      HealthCheckName.TELEMETRY_AGGREGATOR,
      HealthCheckName.CLICKHOUSE,
      HealthCheckName.CLICKHOUSE_PVC_UTILIZATION,
      HealthCheckName.VICTORIA_METRICS,
      HealthCheckName.VICTORIA_METRICS_PVC_UTILIZATION,
    ],
  },
  {
    id: "metrics-database",
    title: "Metrics database",
    checks: [HealthCheckName.METRICS_TIMESCALE_DB, HealthCheckName.METRICS_BACKUPS],
  },
  {
    id: "postgres",
    title: "Postgres (CNPG)",
    checks: [HealthCheckName.PG_CRON],
  },
  {
    id: "storage-buckets",
    title: "Storage buckets",
    checks: [
      HealthCheckName.SOURCE_BUCKET,
      HealthCheckName.DATASET_BUCKET,
      HealthCheckName.PLAN_STAGES_BUCKET,
    ],
  },
  {
    id: "container-registries",
    title: "Container registries",
    checks: [
      HealthCheckName.BASE_IMAGE_CONTAINER_REGISTRY,
      HealthCheckName.COMPUTE_CONTAINER_REGISTRY,
      HealthCheckName.PUSH_CONTAINER_REGISTRY,
    ],
  },
  {
    id: "background-persistence",
    title: "Background persistence",
    checks: [
      HealthCheckName.BACKGROUND_PERSISTENCE_DISK_UTILIZATION,
      HealthCheckName.BACKGROUND_PERSISTENCE_BUS_CONNECTIONS,
      HealthCheckName.PUB_SUB,
    ],
  },
  {
    id: "dataplane-api-server",
    title: "Dataplane API server",
    checks: [
      HealthCheckName.DATAPLANE_API_SERVER,
      HealthCheckName.VOLUME_SERVICE,
      HealthCheckName.FUNCTION_QUEUE,
    ],
  },
  {
    id: "platform-services",
    title: "Platform services",
    checks: [
      HealthCheckName.LLM_ROUTER,
      HealthCheckName.MCP_GATEWAY,
      HealthCheckName.GRPC_BRANCH_SERVER,
      HealthCheckName.OFFLINE_STORE_QUERY_VALUE_PERSISTENCE,
      HealthCheckName.ARGO,
      HealthCheckName.BILLING_SERVICE,
    ],
  },
];

// Checks deliberately absent from HEALTH_CHECK_GROUPS because the connections
// page gives each of them a dedicated card. The healthcheck-monitor picker
// shows them as its own leading section instead.
export const CORE_SERVICES_HEALTH_CHECK_GROUP: HealthCheckGroup = {
  id: "core-services",
  title: "Core services",
  checks: [
    HealthCheckName.HTTP_ENGINE,
    HealthCheckName.GRPC_ENGINE,
    HealthCheckName.API_SERVER,
    HealthCheckName.ONLINE_STORE,
    HealthCheckName.OFFLINE_STORE,
    HealthCheckName.BRANCH_SERVER,
    HealthCheckName.STREAMING_SERVER,
    HealthCheckName.CLOUD_ACCOUNT,
    HealthCheckName.CLOUD_NETWORKING,
  ],
};
